/**
 * Sentry Error Tracking Plugin (Client-side only)
 *
 * Initializes Sentry for error tracking when SENTRY_DSN is configured.
 * Only runs in production to avoid polluting error logs during development.
 *
 * To enable:
 * 1. Install: npm install @sentry/vue
 * 2. Set NUXT_PUBLIC_SENTRY_DSN (or SENTRY_DSN) in your environment
 */

import { initSentryClient, setUser } from "~/utils/error-tracking";

export default defineNuxtPlugin({
  name: "sentry",
  // Remove enforce: "pre" to run after Pinia is initialized
  async setup(nuxtApp) {
    // Initialize if DSN is configured (works in development too)
    const config = useRuntimeConfig();
    const dsn = String(config.public?.sentryDsn || "").trim();

    if (import.meta.dev) {
      console.log("[Sentry Plugin] Runtime config:", {
        hasDsn: !!dsn,
        dsnPreview: dsn ? dsn.substring(0, 50) + "..." : "none",
      });
    }

    if (!dsn) {
      if (import.meta.dev) {
        console.log("[Sentry Plugin] No DSN found, skipping initialization");
      }
      return;
    }

    // Initialize Sentry
    await initSentryClient(nuxtApp.vueApp, dsn);

    // Set user context when auth changes (wrapped in try-catch for safety)
    try {
      const authStore = useAuthStore();

      watch(
        () => authStore.user,
        (user) => {
          if (user) {
            setUser({
              id: user.id,
              email: user.email,
              role: user.role,
            });
          } else {
            setUser(null);
          }
        },
        { immediate: true }
      );
    } catch (error) {
      if (import.meta.dev) {
        console.warn(
          "[Sentry Plugin] Could not set up auth store watcher:",
          error
        );
      }
      // Continue anyway - Sentry will work without user context
    }
  },
});
